/* ACTION TYPES */
import type Immutable from 'immutable';
import type {
    OpaqueVideoData,
    TemplateBundle as LuminaryTemplateBundle,
    TemplateColor,
} from '@lumen5/luminary';

import type { SceneDesign } from 'js/src/client';

export type PaletteData = {
    name: string;
    colors: TemplateColor[];
};

/**
 * Per-template overrides for the colors charts use. Keyed (as strings) by the effective template
 * palette slot the chart draws with. Each value is either another template color reference
 * (`themeRef_colors_M`) or a custom hex color (`#rrggbb`).
 */
export type ChartColorOverrides = Immutable.Map<string, string>;

/**
 * This is prefixed with "QA" since we also define `TemplateBundle` in luminary but it has a slightly different shape for the QA app.
 */
export type QATemplateBundle = LuminaryTemplateBundle & {
    templateData: OpaqueVideoData;
    sceneDesigns: {
        [key: string]: SceneDesign;
    };
    paletteDatas: PaletteData[];
    textMinSize: number;
    textMaxSize: number;
};

export enum TEMPLATES_ACTIONS {
    // template categories
    STORE_TEMPLATE_CATEGORIES = 'STORE_TEMPLATE_CATEGORIES',
    // templates
    STORE_TEMPLATES = 'STORE_TEMPLATES',
    STORE_TEMPLATE = 'STORE_TEMPLATE',
    SET_TEMPLATE_DATA = 'SET_TEMPLATE_DATA',
    SET_TEMPLATE_STATUS = 'SET_TEMPLATE_STATUS',
    SET_TEMPLATE_COLOR_PALETTES = 'SET_TEMPLATE_COLOR_PALETTES',
    SET_TEMPLATE_CHART_COLOR_OVERRIDES = 'SET_TEMPLATE_CHART_COLOR_OVERRIDES',
    SET_TEMPLATE_DISPLAY_NAME = 'SET_TEMPLATE_DISPLAY_NAME',
    SET_TEMPLATE_DEFAULT_STORYBOARD_SCENE_DESIGN = 'SET_TEMPLATE_DEFAULT_STORYBOARD_SCENE_DESIGN',
    SET_TEMPLATE_DEFAULT_OUTRO_SCENE_DESIGN = 'SET_TEMPLATE_DEFAULT_OUTRO_SCENE_DESIGN',
    SET_TEMPLATE_DEFAULT_ICON_SCENE_DESIGN = 'SET_TEMPLATE_DEFAULT_ICON_SCENE_DESIGN',
    SET_TEMPLATE_DEFAULT_SCENE_DESIGN = 'SET_TEMPLATE_DEFAULT_SCENE_DESIGN',
    SET_TEMPLATE_IMAGE_URL = 'SET_TEMPLATE_IMAGE_URL',
    SET_TEMPLATE_MP4_URL = 'SET_TEMPLATE_MP4_URL',
    SET_TEMPLATE_ASPECT_RATIO_MP4_URL = 'SET_TEMPLATE_ASPECT_RATIO_MP4_URL',
    SET_TEMPLATE_TEXT_MIN_SIZE = 'SET_TEMPLATE_TEXT_MIN_SIZE',
    SET_TEMPLATE_TEXT_MAX_SIZE = 'SET_TEMPLATE_TEXT_MAX_SIZE',
    SET_TEMPLATE_ORDERS = 'SET_TEMPLATE_ORDERS',
    SET_TEMPLATE_CATEGORY_ID = 'SET_TEMPLATE_CATEGORY_ID',
    SET_TEMPLATE_DEFAULT_TEXT_SIZE_OPTIONS = 'SET_TEMPLATE_DEFAULT_TEXT_SIZE_OPTIONS',
    SET_TEMPLATE_DEFAULT_TALKING_HEAD_SCENE_DESIGN = 'SET_TEMPLATE_DEFAULT_TALKING_HEAD_SCENE_DESIGN',
    SET_TEMPLATE_DEFAULT_CAPTION_SCENE_DESIGN = 'SET_TEMPLATE_DEFAULT_CAPTION_SCENE_DESIGN',
    SET_TEMPLATE_DEFAULT_VOICEOVER_SCENE_DESIGN = 'SET_TEMPLATE_DEFAULT_VOICEOVER_SCENE_DESIGN',
    SET_TEMPLATE_DEFAULT_CUTAWAY_SCENE_DESIGN = 'SET_TEMPLATE_DEFAULT_CUTAWAY_SCENE_DESIGN',
    SET_TEMPLATE_DEFAULT_ASPECT_RATIO = 'SET_TEMPLATE_DEFAULT_ASPECT_RATIO',
    // scene designs
    STORE_SCENE_DESIGNS = 'STORE_SCENE_DESIGNS',
    STORE_SCENE_DESIGN = 'STORE_SCENE_DESIGN',
    STORE_ALL_SCENE_DESIGN_IDS_PER_TEMPLATE = 'STORE_ALL_SCENE_DESIGN_IDS_PER_TEMPLATE',
    STORE_RECENT_SELECTED_SCENE_DESIGN = 'STORE_RECENT_SELECTED_SCENE_DESIGN',
    SET_SCENE_DESIGN_ATTRIBUTES_RECOMMENDATION = 'SET_SCENE_DESIGN_ATTRIBUTES_RECOMMENDATION',
    SET_SCENE_DESIGN_DATA = 'SET_SCENE_DESIGN_DATA',
    SET_SCENE_DESIGN_STATUS = 'SET_SCENE_DESIGN_STATUS',
    SET_SCENE_DESIGN_ORDERS = 'SET_SCENE_DESIGN_ORDERS',
    STORE_DISPLAY_GROUPS = 'STORE_DISPLAY_GROUPS',
    SET_DISPLAY_GROUP_NAME = 'SET_DISPLAY_GROUP_NAME',
    SET_SCENE_DESIGN_DISPLAY_GROUP = 'SET_SCENE_DESIGN_DISPLAY_GROUP',
    SET_ACTIVE_TEMPLATE_CATEGORY = 'SET_ACTIVE_TEMPLATE_CATEGORY',
    SET_TEMPLATE_COLOR_SHUFFLE_ENABLED = 'SET_TEMPLATE_COLOR_SHUFFLE_ENABLED',
    SET_TEMPLATE_ALLOW_GLOBAL_SCENE_DESIGNS = 'SET_TEMPLATE_ALLOW_GLOBAL_SCENE_DESIGNS',
    SET_TEMPLATE_ENABLED_GLOBAL_TEMPLATES = 'SET_TEMPLATE_ENABLED_GLOBAL_TEMPLATES',
    SET_TEMPLATE_IS_GLOBAL_TEMPLATE = 'SET_TEMPLATE_IS_GLOBAL_TEMPLATE',
    SET_SCENE_DESIGN_DISPLAY_NAME = 'SET_SCENE_DESIGN_DISPLAY_NAME',
    SET_NON_RECOMMENDABLE_ASSETS = 'SET_NON_RECOMMENDABLE_ASSETS',
    SET_TEMPLATE_SEARCH_QUERY = 'SET_TEMPLATE_SEARCH_QUERY',
    CLEAR_SCENE_DESIGNS_FOR_TEMPLATES = 'CLEAR_SCENE_DESIGNS_FOR_TEMPLATES',
}

export type SceneDesignId = string;
export type SceneDesignDisplayName = string;
export type DisplayGroupId = number;
